import { create } from "zustand";
import { axiosHelper, tokenHelper } from "../helpers";
import { setCookie, removeCookie, getCookie } from "../../helpers/cookies";
import { decrypt, encrypt } from "../../helpers/sessions";
import { normalizePermissions } from "../../helpers/planLimits";

export const useAuth = create((set) => ({
  user: null,
  accessToken: null,
  successMsg: null,
  isLoading: false,
  error: null,
  errorMsg: null,

  setError: (msg) => set({ errorMsg: msg, successMsg: null, isLoading: false }),
  setSuccess: (msg) =>
    set({ successMsg: msg, errorMsg: null, isLoading: false }),
  clearMessages: () => set({ successMsg: null, errorMsg: null }),
  verifyToken: async () => {
    const session = getCookie("session");
    const payload = await decrypt(session);

    if (!payload || !payload?.accessToken) {
      removeCookie("session");
      tokenHelper.removeToken();
      tokenHelper.removeUser();
      return false;
    }

    const verifyAccessToken = await axiosHelper.post(
      "/user/auth/validate-token",
      {
        token: payload.accessToken,
      }
    );

    if (!verifyAccessToken?.success) {
      removeCookie("session");
      tokenHelper.removeToken();
      tokenHelper.removeUser();
      return false;
    }

    // Sync store state
    set({ user: payload.user, accessToken: payload.accessToken });

    return payload; // Return the payload if the token is valid
  },
  login: async (data, router) => {
    set({ isLoading: true, errorMsg: null, successMsg: null });

    try {
      const response = await axiosHelper.post(
        "/user/auth/login-portal",
        JSON.stringify(data)
      );

      const { accessToken, permissions } = response;

      if (!accessToken) throw new Error("Invalid response from authentication");

      const response2 = await axiosHelper.get("/user/infos", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (["USER", "NORMAL_USER"].includes(response2.role?.toUpperCase())) {
        set({ errorMsg: "Not Allowed", isLoading: false });
        return;
      }

      const expiresAt = new Date("2099-12-31T23:59:59Z");
      const session = await encrypt({
        accessToken,
        expiresAt,
        user: response2,
        permissions: permissions,
      });

      tokenHelper.setToken(accessToken);
      setCookie("session", session, {
        expires: expiresAt,
        secure: true,
        sameSite: "lax",
      });

      set({ successMsg: "Login Successful", isLoading: false });

      if (response2.onboardingCompleted === false) {
        // Check onboarding status and redirect accordingly
        const step = response2.onboardingStep || 1;
        router.navigate(`/onboarding?step=${step}`);
      } else {
        router.navigate("/dashboard");
      }
    } catch (error) {
      set({ errorMsg: error.response?.data?.message, isLoading: false });
    }
  },

  logout: () => {
    removeCookie("session");
    tokenHelper.removeToken();
    tokenHelper.removeUser();
  },

  signup: async (data) => {
    set({ isLoading: true });

    try {
      await axiosHelper.post("/v2/user/auth/register-admin", {
        name: data.name,
        email: data.email,
        password: data.password,
        phone: data.phone,
        // voucherCode: data.voucherCode,
        userLanguage: "EN",
        max_daily_orders: 20,
        phoneOS: "android",
      });
      set({ successMsg: "Registred Successfully", isLoading: false });
    } catch (error) {
      set({
        errorMsg: "Email already registered, use another email",
        isLoading: false,
      });
    }
  },

  forgetPassword: async (data, router) => {
    set({ isLoading: true });

    try {
      await axiosHelper.post("/v2/user/auth/forgot-password", {
        email: data.email,
      });
      set({
        successMsg: "Reset password link sent to your email",
        isLoading: false,
      });
      router.navigate("/reset-password");
    } catch (error) {
      set({ errorMsg: "Error sending reset password link", isLoading: false });
    }
  },

  resetPassword: async (data, router) => {
    set({ isLoading: true });

    try {
      await axiosHelper.post("/v2/user/auth/reset-password", {
        email: data.email,
        newPassword: data.password,
        otp: data.otp,
      });
      set({ successMsg: "Password reset successfully", isLoading: false });
    } catch (error) {
      set({ errorMsg: "Invalid OTP", isLoading: false });
    }
  },

  validateEmail: async (data) => {
    set({ isLoading: true });

    try {
      await axiosHelper.get(`/user/auth/validate-email/${data.token}`);
      set({ successMsg: "Email Validated Successfully", isLoading: false });
    } catch (error) {
      set({ errorMsg: "Error Validating Email", isLoading: false });
    }
  },

  refreshUser: async () => {
    try {
      const accessToken = await tokenHelper.getToken();
      if (!accessToken) return;

      const response2 = await axiosHelper.get("/user/infos", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response2) {
        console.warn("Refresh user: No response from /user/infos");
        return;
      }

      const sessionRaw = getCookie("session");
      const payload = await decrypt(sessionRaw);

      if (payload) {
        const expiresAt = new Date("2099-12-31T23:59:59Z");
        const permissions =
          normalizePermissions(response2.permission) ?? response2.permissions ?? payload.permissions ?? {};
        const session = await encrypt({
          accessToken,
          expiresAt,
          user: response2,
          permissions,
        });

        setCookie("session", session, {
          expires: expiresAt,
          secure: true,
          sameSite: "lax",
        });
        set({ user: response2 });
        return response2;
      }
    } catch (e) {
      console.error("Refresh user failed", e);
    }
  }
}));
